import NumberFlow from "@number-flow/react"
import { Flex, Progress, Tooltip, Typography } from "antd"
import cls from "classnames"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import InfoTooltip from "../../../../../../../Components/InfoTooltip/InfoTooltip"
import { RoutesPaths } from "../../../../../../../constants/user-routes"
import useAnimatedNumber from "../../../../../../../hooks/useAnimatedNumber"
import { SECTION_LABELS } from "../../../../ConfigurePage/schema/sections"
import { SECTION_TO_CONFIGURE } from "../../../constants/sectionRouting"
import { titleCase } from "../../../utils/titleCase"

import groupStyles from "../../group.module.less"
import tabStyles from "../detailsTab.module.less"
import styles from "./enrichmentBreakdown.module.less"

const { Text } = Typography

const sectionLabel = (s) => s.title || titleCase(s.key)
const configureStepFor = (s) => SECTION_TO_CONFIGURE[s.key] || "required_information"
// Where a weak section is fixed, so a chip click lands somewhere useful: a file goes to
// the Evidence locker, everything else is a typed answer in its Configure step. The
// letter's prose blocks used to route to the PDF editor; with the editor gone (RCV-5-36)
// they are Configure answers like the rest — evidence changes by editing the profile and
// rebuilding, never by hand.
const isLockerSection = (s) => SECTION_TO_CONFIGURE[s.key] === "evidence_vault"

const bandColor = (pct) => (pct >= 67 ? "#16895A" : pct >= 34 ? "#DF7009" : "#D71313")

// Completeness against the sections this dispute SHOULD contain, computed on the server
// (recover.serializers.enrichment_breakdown) so this block, the /recover/all column and
// the CSV never drift. Two groups of gaps: `fixable` are merchant-fixable, so they are
// clickable chips that route to Configure/locker; `dataLimited` are sections whose only
// gap is processor data the merchant cannot supply, so they read as read-only and the %
// still reads honestly (a complete profile can always reach 100).
const EnrichmentBreakdown = ({ breakdown, isActionable }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const animatedPct = useAnimatedNumber(breakdown?.pct ?? 0)
  const fixable = breakdown?.fixable || []
  const dataLimited = breakdown?.data_limited || []

  const fixTooltip = (s) => {
    const title = sectionLabel(s)
    return isLockerSection(s)
      ? t("_title_: upload it to your Evidence locker to strengthen the PDF.", { title })
      : t("_title_: add it in the _step_ step to strengthen the PDF.", {
          title,
          step: t(SECTION_LABELS[configureStepFor(s)] || "Your profile")
        })
  }
  const handleFix = (s) => navigate(RoutesPaths.recover.configure_section(configureStepFor(s)))

  return (
    <div className={cls(groupStyles.groupCard, groupStyles.groupCardSpaced)}>
      <div className={groupStyles.groupLabel}>
        {t("Enrichment")}
        <InfoTooltip
          title={
            isActionable
              ? t(
                  "How complete your evidence is, measured against every section this dispute should include. Fill the gaps below to strengthen it."
                )
              : t("How complete your evidence was when submitted.")
          }
        />
      </div>
      {/* Same battery-gauge presentation as the All-table's Enrichment cell
          (level-coloured bar + %), so the score reads identically everywhere. */}
      <div className={cls(styles.win, styles.winBar)}>
        <span className="dispute-enrichment-cell">
          <Progress
            percent={Math.max(0, Math.min(100, Math.round(animatedPct)))}
            showInfo={false}
            strokeColor={bandColor(animatedPct)}
            className="dispute-enrich-bar"
          />
          <Text className="dispute-enrichment-pct">
            <NumberFlow value={animatedPct} suffix="%" format={{ maximumFractionDigits: 0 }} />
          </Text>
        </span>
        <Text type="secondary" className={styles.winCap}>
          {t("_n_ of _total_ sections complete", {
            n: breakdown.complete_count,
            total: breakdown.addressable_total
          })}
        </Text>
      </div>
      {fixable.length > 0 && isActionable ? (
        <div className={styles.strengthen}>
          <Text type="secondary" className={tabStyles.hint}>
            {t("Add detail to strengthen:")}
          </Text>
          <Flex gap={8} wrap="wrap" className={styles.chips}>
            {fixable.map((s) => (
              <Tooltip key={s.key} title={fixTooltip(s)}>
                <span
                  className={styles.chip}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleFix(s)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault()
                      handleFix(s)
                    }
                  }}
                >
                  {sectionLabel(s)}
                </span>
              </Tooltip>
            ))}
          </Flex>
        </div>
      ) : null}
      {dataLimited.length > 0 ? (
        <div className={styles.strengthen}>
          <Text type="secondary" className={tabStyles.hint}>
            {t("Not available on this transaction:")}
          </Text>
          <Flex gap={8} wrap="wrap" className={styles.chips}>
            {dataLimited.map((s) => (
              <Tooltip
                key={s.key}
                title={t(
                  "_title_: this relies on data the processor did not provide for this charge. Nothing to add.",
                  { title: sectionLabel(s) }
                )}
              >
                <span className={cls(styles.chip, styles.readonly)}>{sectionLabel(s)}</span>
              </Tooltip>
            ))}
          </Flex>
        </div>
      ) : null}
    </div>
  )
}

export default EnrichmentBreakdown
